/*
 * This is wrapper component that creates a JS script tag for a given Backbone module
 * and adds it to the bottom of the body.
 * This is to replace the current structure with Backbone of each separate .html page
 * having its own script tag
 * Since are using routes and only one index.html, we need dynamically add the needed script tag for each route component
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

export const ModuleBridge = ({ moduleId }) => {
    const [error, setError] = useState(null);
    useEffect(() => {
        let isMounted = true;
        const script = document.createElement('script');
        const loadModule = async () => {
            try {
                // Create and load the script
                // process.env.BUILD_NUMBER is replaced at compile time by Webpack from the webpack config file
                script.src = `/reactor/js/modules/${moduleId}.js?v=${process.env.BUILD_NUMBER}`;
                script.type = 'text/javascript';
                // Promise to handle script loading
                await new Promise((resolve, reject) => {
                    script.onload = resolve;
                    script.onerror = reject;
                    document.body.appendChild(script);
                });
                // Initialize module if it exists
                if (window[moduleId]?.initialize) {
                    window[moduleId].initialize();
                }
            } catch (err) {
                if (isMounted) {
                    console.error(`Failed to load module: ${moduleId}`, err);
                    setError(err);
                }
            }
        };
        loadModule();
        // Cleanup function
        return () => {
            isMounted = false;
            // Call module's cleanup if it exists
            if (window[moduleId]?.cleanup) {
                try {
                    window[moduleId].cleanup();
                } catch (err) {
                    console.error(`Error during ${moduleId} cleanup:`, err);
                }
            }
            // Clean up global module reference
            delete window[moduleId];
            document.body.removeChild(script);
        };
    }, [moduleId]);
    if (error) {
        return <div className="module-error">Failed to load module: {moduleId}</div>;
    }
    return <div id={`${moduleId}-container`} />;
};

ModuleBridge.propTypes = {
    moduleId: PropTypes.string.isRequired,
};
